import { getClassName } from "@pairtreefamily/utils";
import React, { useRef, useState } from "react";
import { InView } from "react-intersection-observer";
import { priceFormatter } from "../../utils/priceFormatter";
import { ProductPricingResponse } from "../../types/coreApiMirror";
import { Colors } from "../../types/colors";
import { PricingLandingData } from "../../queries/pricingLandingQuery";
import BestDealGraphic from "../icons/bestDealGraphic";
import { State } from "../../types/state";

function getProductPrice(
  currentPrices: ProductPricingResponse[],
  coreProductCode: string,
): ProductPricingResponse[] {
  return currentPrices?.filter(
    (item) =>
      item.internalId.replace("_monthly", "").replace("_annual", "") ==
      coreProductCode,
  );
}

const colorMap = {
  darkblue: "text-darkblue",
  teal: "text-teal",
  green: "text-green",
  lightgreen: "text-lightgreen",
  peach: "text-peach",
  rust: "text-rust",
};

const colorBgMap = {
  darkblue: "bg-darkblue",
  teal: "bg-teal",
  green: "bg-green",
  lightgreen: "bg-lightgreen",
  peach: "bg-peach",
  rust: "bg-rust",
};

const colorBorderMap = {
  darkblue: "border-darkblue",
  teal: "border-teal",
  green: "border-green",
  lightgreen: "border-lightgreen",
  peach: "border-peach",
  rust: "border-rust",
};
const colorFilterMap = {
  darkblue: "filter-darkblue",
  teal: "filter-teal",
  green: "filter-green",
  lightgreen: "filter-lightgreen",
  peach: "filter-peach",
  rust: "filter-rust",
};

type PricingColor = Extract<
  Colors.Solid,
  "darkblue" | "teal" | "green" | "lightgreen" | "peach" | "rust"
>;

export type ProductsComparisonTableHeader = {
  moduleInfo: Pick<PricingLandingData, "moduleProductsComparison">;
  selectedState?: State;
  prices: ProductPricingResponse[];
  stickyHeader: React.RefObject<HTMLTableElement>;
  updateShowHeader: (value: boolean) => void;
};

export function ProductsComparisonTableHeader(
  props: ProductsComparisonTableHeader,
) {
  const moduleInfo = props.moduleInfo.moduleProductsComparison.moduleInput;

  const { selectedState, prices, stickyHeader, updateShowHeader } = props;

  const [viewType, setViewType] = useState<"month" | "year">("month");

  const getMostExpensiveProduct = (
    items: ProductPricingResponse[],
  ): ProductPricingResponse => {
    return items.sort(({ unit_amount: a }, { unit_amount: b }) => b - a)[0];
  };

  // console.log(prices, "prices")
  // console.log(moduleInfo, "prices")

  return (
    <>
      <React.Fragment>
        <InView threshold={1}>
          {({ inView, ref, entry }) => {
            if (
              entry &&
              entry.target.id === "tableHeader" &&
              !entry.isIntersecting &&
              stickyHeader?.current?.getBoundingClientRect() &&
              stickyHeader?.current?.getBoundingClientRect()?.top <= 74
            ) {
              updateShowHeader(true);
            } else {
              updateShowHeader(false);
            }

            return (
              <>
                <thead ref={ref} id="tableHeader" className="table w-full">
                  <tr className="w-full">
                    <th
                      className="align-bottom"
                      style={{
                        width: `calc(100% / (${
                          (moduleInfo.products?.length ?? 0) + 1
                        })`,
                      }}
                    >
                      <h2 className="text-overline-2 text-left">
                        {moduleInfo.heading}
                      </h2>
                      <h3 className="text-head-3 mb-12 text-left">
                        <em>{moduleInfo.subHeading}</em>
                      </h3>
                    </th>

                    {moduleInfo.products?.map((product, index) => {
                      const productPrice = getProductPrice(
                        prices,
                        product?.product?.coreProductCode ?? "",
                      );

                      const monthPrice = getMostExpensiveProduct(
                        productPrice?.filter(
                          (item) =>
                            item.type === "recurring" &&
                            item.recurring.interval === "month",
                        ),
                      );
                      const yearPrice = getMostExpensiveProduct(
                        productPrice?.filter(
                          (item) =>
                            item.type === "recurring" &&
                            item.recurring.interval === "year",
                        ),
                      );
                      const oneTimePrice = getMostExpensiveProduct(
                        productPrice?.filter(
                          (item) => item.type === "one_time",
                        ),
                      );
                      return (
                        <>
                          <th
                            key={product?.product?._id}
                            className={getClassName(
                              "align-top",
                              (getProductPrice(prices, "complete").length ==
                                0 &&
                                product?.product?.coreProductCode !==
                                  "connect_plus") ||
                                (getProductPrice(prices, "complete").length >
                                  0 &&
                                  product?.product?.coreProductCode !==
                                    "complete")
                                ? ""
                                : (index + 1) % 2 == 0
                                  ? ""
                                  : "bg-[#f8f8f8]",
                            )}
                            style={{
                              width: `calc(100% / (${
                                (moduleInfo.products?.length ?? 0) + 2
                              })`,
                            }}
                          >
                            <div
                              className={getClassName(
                                "relative border-t-[4px] px-4 pt-4",
                                colorBorderMap[
                                  product?.product?.color as PricingColor
                                ],
                                (index + 1) % 2 == 0 ? "" : "bg-[#f8f8f8]",
                                (getProductPrice(prices, "complete").length ==
                                  0 &&
                                  product?.product?.coreProductCode !==
                                    "connect_plus") ||
                                  (getProductPrice(prices, "complete").length >
                                    0 &&
                                    product?.product?.coreProductCode !==
                                      "complete")
                                  ? //the box shadow here is being used as a hack to fill a gap in the color between the header and the body
                                    `mt-[30px] ${
                                      (index + 1) % 2 == 0
                                        ? ""
                                        : "shadow-[0_60px_0_0_rgba(248,248,248,1)]"
                                    }`
                                  : "[&_.price]:mt-[30px]",
                              )}
                            >
                              <p className="font-rebrand text-heading-20 text-black">
                                <em>PairTree</em>
                              </p>
                              <h2 className="font-rebrand text-heading-30">
                                <em className="relative">
                                  {product?.product?.name}
                                  {product?.product?.name?.includes("plus") && (
                                    <img
                                      className={getClassName(
                                        "absolute right-0 ",
                                        colorFilterMap[
                                          product?.product
                                            ?.color as PricingColor
                                        ],
                                      )}
                                      src="/plus_graphic.svg"
                                    />
                                  )}
                                </em>
                              </h2>

                              {oneTimePrice && productPrice.length && (
                                <>
                                  <p
                                    className={getClassName(
                                      "text-head-8 price text-center",
                                      colorMap[
                                        product?.product?.color as PricingColor
                                      ],
                                    )}
                                  >
                                    {priceFormatter(
                                      oneTimePrice.unit_amount / 100,
                                    )}
                                  </p>
                                  <p className="text-body-3 text-center">
                                    Pricing starts at
                                  </p>
                                </>
                              )}
                              {(monthPrice || yearPrice) &&
                                productPrice.length && (
                                  <>
                                    {viewType === "month" && (
                                      <p className="text-center">
                                        <span
                                          className={getClassName(
                                            "text-head-8 price text-center",
                                            colorMap[
                                              product?.product
                                                ?.color as PricingColor
                                            ],
                                          )}
                                        >
                                          {priceFormatter(
                                            monthPrice.unit_amount / 100,
                                          )}
                                        </span>
                                        <span className="text-body-3">
                                          &nbsp;/ month
                                        </span>
                                      </p>
                                    )}
                                    {viewType === "year" && (
                                      <p className="text-center">
                                        <span
                                          className={getClassName(
                                            "text-head-8 price text-center",
                                            colorMap[
                                              product?.product
                                                ?.color as PricingColor
                                            ],
                                          )}
                                        >
                                          {priceFormatter(
                                            yearPrice.unit_amount / 100,
                                          )}
                                        </span>
                                        <span className="text-body-3">
                                          &nbsp;/ year
                                        </span>
                                      </p>
                                    )}
                                    <div className="text-body-4-semi cursor-pointer p-4 text-center">
                                      <div
                                        className={getClassName(
                                          "flex justify-around rounded-[80px] border",
                                          colorBorderMap[
                                            product?.product
                                              ?.color as PricingColor
                                          ],
                                        )}
                                      >
                                        <span
                                          className={getClassName(
                                            "w-full rounded-[80px] py-[2px]",
                                            viewType === "month" &&
                                              `${
                                                colorBgMap[
                                                  product?.product
                                                    ?.color as PricingColor
                                                ]
                                              } text-white`,
                                          )}
                                          onClick={() => setViewType("month")}
                                        >
                                          Monthly
                                        </span>
                                        <span
                                          className={getClassName(
                                            "w-full rounded-[80px] py-[2px]",
                                            viewType === "year" &&
                                              `${
                                                colorBgMap[
                                                  product?.product
                                                    ?.color as PricingColor
                                                ]
                                              } text-white`,
                                          )}
                                          onClick={() => setViewType("year")}
                                        >
                                          Yearly
                                        </span>
                                      </div>
                                    </div>
                                  </>
                                )}
                              {getProductPrice(prices, "complete").length > 0 &&
                                product?.product?.coreProductCode ===
                                  "complete" && (
                                  <div className="absolute right-[-30px] top-[-38px]">
                                    <BestDealGraphic
                                      className={
                                        colorMap[
                                          product?.product
                                            ?.color as PricingColor
                                        ]
                                      }
                                    />
                                  </div>
                                )}

                              {getProductPrice(prices, "complete").length ===
                                0 &&
                                product?.product?.coreProductCode ===
                                  "connect_plus" && (
                                  <div className="absolute right-[-30px] top-[-38px]">
                                    <BestDealGraphic
                                      className={
                                        colorMap[
                                          product?.product
                                            ?.color as PricingColor
                                        ]
                                      }
                                    />
                                  </div>
                                )}
                            </div>
                          </th>
                        </>
                      );
                    })}
                  </tr>
                </thead>
              </>
            );
          }}
        </InView>
      </React.Fragment>
    </>
  );
}
